// Phase JA.3 — Intelligence Signal Bar.
//
// Compact horizontal bar shown ABOVE the Signal Menu. Surfaces today's
// live counts per tier, the suppressed-candidates count framed as
// quality discipline, and the Founder's Pick state.
//
// Doctrine:
//   - Promotion-not-restriction: suppressions are "rejected (quality
//     discipline)", never "failures".
//   - No fabrication: zero counts render honestly.

import { CategoryChip, Eyebrow, Pill } from "../super-acca";
import type { IntelTodayCounts } from "../../free-tier/useDailyAccaIntel.ts";

export interface IntelligenceSignalBarProps {
  counts: IntelTodayCounts;
  loading?: boolean;
}

function tierColor(tier: "a" | "b" | "c"): string {
  if (tier === "a") return "var(--sa-signal-green)";
  if (tier === "b") return "var(--sa-accent-saffron)";
  return "var(--sa-signal-info)";
}

function TierPill({
  tier,
  count,
}: {
  tier: "a" | "b" | "c";
  count: number;
}): JSX.Element {
  const color = tierColor(tier);
  return (
    <Pill
      color={color}
      borderColor={`${color}55`}
      background={`${color}14`}
      style={{ gap: 8 }}
    >
      <span style={{ fontWeight: 600 }}>TIER {tier.toUpperCase()}</span>
      <span
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 11,
          color,
          letterSpacing: "0.04em",
        }}
      >
        {count}
      </span>
    </Pill>
  );
}

export function IntelligenceSignalBar({
  counts,
  loading,
}: IntelligenceSignalBarProps): JSX.Element {
  const suppressedLabel =
    counts.total_suppressed === 0
      ? "0 candidates rejected"
      : `${counts.total_suppressed} candidate${counts.total_suppressed === 1 ? "" : "s"} rejected (quality discipline)`;

  return (
    <section
      aria-label="Intelligence Signal Bar"
      data-testid="intelligence-signal-bar"
      style={{
        margin: "0 auto",
        maxWidth: 1180,
        padding: "16px 48px 8px",
      }}
    >
      <Eyebrow style={{ marginBottom: 10 }}>
        Intelligence · Live{loading ? " · sync" : ""}
      </Eyebrow>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          padding: "12px 14px",
          border: "1px solid var(--sa-hairline)",
          borderRadius: 4,
        }}
      >
        <TierPill tier="a" count={counts.tier_a_live} />
        <TierPill tier="b" count={counts.tier_b_live} />
        <TierPill tier="c" count={counts.tier_c_live} />
        <span
          aria-hidden
          style={{ width: 1, height: 18, background: "var(--sa-hairline)", margin: "0 4px" }}
        />
        {counts.founders_pick_live ? (
          <CategoryChip category="ANCHOR" />
        ) : (
          <Pill color="var(--sa-fg-muted)" borderColor="var(--sa-hairline)">
            Founder&apos;s Pick · pending
          </Pill>
        )}
        <span style={{ flex: 1 }} />
        <Pill
          color="var(--sa-fg-secondary)"
          borderColor="var(--sa-hairline)"
          background="transparent"
        >
          {suppressedLabel}
        </Pill>
      </div>
    </section>
  );
}
